import React from 'react';

import { Box, Chip, Grid } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { useForm } from 'react-final-form';

import { DateFormatPatterns } from '@clearverify/constants';

import { DateRangePicker, DateRangeValue } from 'src/components/mui/DateRangePicker';
import { SxProp } from 'src/types/mui';
import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

import { defaultRangeShortcuts, RANGE_SHORTCUT_LABEL_MAP, RangeShortcut } from './constants';
import { DateRangeFieldProps } from './interface';

export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  input: { name, value, onChange },
  meta,
  inputFormat = DateFormatPatterns.shortDateWithSlash,
  fullWidth = false,
  required = false,
  disabled = false,
  label,
  warning,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  textFieldSxProps = {},
  rangeShortcuts = defaultRangeShortcuts,
  minDate,
  maxDate,
}) => {
  const { blur } = useForm();

  const handleChange = React.useCallback(
    (date: DateRangeValue) => {
      onChange([date[0]?.toISODate() ?? null, date[1]?.toISODate() ?? null]);
      // Mark the field touched only once the range is complete — a half-picked range under a
      // still-open popper is mid-interaction and must not surface "required" errors yet.
      if (date[0] && date[1]) blur(name);
    },
    [name, blur, onChange],
  );

  const handleRangeClick = React.useCallback(
    (range: RangeShortcut) => buildHandleRangeClick(handleChange, minDate, maxDate)(range),
    [handleChange, minDate, maxDate],
  );

  const shouldShowError = showErrorWhenDisabled || !disabled;
  const hasError = hasFieldError(meta) && shouldShowError;

  const getHelperText = React.useCallback(() => {
    if (!shouldShowError) return '';

    const fieldError =
      getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled);

    if (fieldError) {
      return fieldError;
    }

    return '';
  }, [disabled, meta, shouldShowError, showErrorOnlyTouched, value, warning]);

  const convertedValue = React.useMemo((): DateRangeValue => {
    if (value === null) return [null, null];
    const startDate = value[0] ? DateTime.fromISO(value[0]) : null;
    const endDate = value[1] ? DateTime.fromISO(value[1]) : null;
    return [startDate?.isValid ? startDate : null, endDate?.isValid ? endDate : null];
  }, [value]);

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <DateRangePicker
          name={name}
          format={inputFormat}
          value={convertedValue}
          textFieldProps={{
            error: hasError,
            helperText: shouldShowError ? getHelperText() : undefined,
            fullWidth,
            // No native onBlur passthrough: focus moving into the calendar popper would mark the
            // field touched mid-pick and flash "required". blur(name) in handleChange covers it.
            autoComplete: 'off',
            required,
            label,
            sx: textFieldSxProps,
          }}
          disabled={disabled}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
        />
      </Grid>
      {!isEmpty(rangeShortcuts) && (
        <Grid sx={rangeShortcutOuterWrapperStyles} size={12}>
          <Box display="inline-flex" gap={1} flexWrap="wrap" sx={rangeShortcutsWrapperStyles}>
            {rangeShortcuts.map(rangeShortcut => {
              return (
                <Chip
                  key={rangeShortcut}
                  label={RANGE_SHORTCUT_LABEL_MAP[rangeShortcut]}
                  sx={{ borderRadius: 4 }}
                  size="small"
                  onClick={() => handleRangeClick(rangeShortcut)}
                />
              );
            })}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

const buildHandleRangeClick =
  (setValue: (date: DateRangeValue) => void, minDate?: DateTime, maxDate?: DateTime) =>
  (range: RangeShortcut) => {
    const clamp = (date: DateTime) => {
      if (minDate && date < minDate) return minDate;
      if (maxDate && date > maxDate) return maxDate;
      return date;
    };
    const setRange = (start: DateTime, end: DateTime) => setValue([clamp(start), clamp(end)]);
    const today = DateTime.now();
    switch (range) {
      case RangeShortcut.twoYears:
        setRange(today.minus({ years: 2 }), today);
        break;
      case RangeShortcut.oneYear:
        setRange(today.minus({ years: 1 }), today);
        break;
      case RangeShortcut.sixMonths:
        setRange(today.minus({ months: 6 }), today);
        break;
      case RangeShortcut.threeMonths:
        setRange(today.minus({ months: 3 }), today);
        break;
      case RangeShortcut.oneMonth:
        setRange(today.minus({ months: 1 }), today);
        break;
      case RangeShortcut.yearToDate:
        setRange(today.startOf('year'), today);
        break;
      default:
        break;
    }
  };

const rangeShortcutsWrapperStyles: SxProp = theme => ({
  [theme.breakpoints.down('sm')]: {
    overflowY: 'scroll',
    maxHeight: '100%',
    width: 'max-content',
    pl: 2,
  },
});

const rangeShortcutOuterWrapperStyles: SxProp = theme => ({
  [theme.breakpoints.down('sm')]: {
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
});
